/**
 * Field Operations API Service
 * Handles location tracking and SP point retrieval for the field map.
 */
import { api } from '../api';

/**
 * Send current GPS location to the server
 */
export const sendLocation = async (data) => {
  const response = await api.post('/field-operations/location', data);
  return response.data;
};

export const sendTrackedPosition = async (data) => {
  const response = await api.post('/field-operations/tracking/positions', data);
  return response.data;
};

/**
 * Get latest locations of all tracked personnel in a project
 */
export const getLatestLocations = async (projectId, minutes = 60) => {
  const response = await api.get(`/field-operations/locations/${projectId}`, {
    params: { minutes }
  });
  return response.data;
};

/**
 * Get SP registry points for map overlay
 */
export const getSPPointsForMap = async (projectId) => {
  const response = await api.get(`/field-operations/sp-points/${projectId}`);
  return response.data;
};

/**
 * Igual, pero mandando el sello que ya tenemos para que el servidor pueda
 * contestar 304 sin cuerpo.
 *
 * El backend publica un `ETag` desde agosto de 2026 y **nadie lo usaba**: la
 * petición lleva `Authorization: Bearer`, el navegador no almacena la respuesta
 * y por tanto nunca manda `If-None-Match`. Verificado en producción. Así que el
 * sello lo lleva el cliente.
 *
 * `validateStatus` es imprescindible: axios rechaza por defecto cualquier cosa
 * fuera de 2xx, y sin esto cada respuesta *sin cambios* llegaría al `catch` y
 * el mapa creería que la carga falló.
 *
 * @returns {{notModified: true}|{notModified: false, data: Object, etag: ?string}}
 */
export const getSPPointsForMapConditional = async (projectId, etag = null) => {
  const response = await api.get(`/field-operations/sp-points/${projectId}`, {
    headers: etag ? { 'If-None-Match': etag } : {},
    validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
  });

  if (response.status === 304) return { notModified: true };
  return {
    notModified: false,
    data: response.data,
    etag: response.headers?.etag ?? null,
  };
};

/**
 * Check if current user can be tracked
 */
export const getTrackingStatus = async (projectId) => {
  const response = await api.get('/field-operations/tracking-status', {
    params: { project_id: projectId }
  });
  return response.data;
};

export const getTrackingConsent = async (projectId) => {
  const response = await api.get('/field-operations/tracking/consent', {
    params: { project_id: projectId },
  });
  return response.data;
};

export const grantTrackingConsent = async (projectId, policyVersion = 'v1.0') => {
  const response = await api.post('/field-operations/tracking/consent/grant', {
    project_id: projectId,
    policy_version: policyVersion,
    granted_via: 'web',
  });
  return response.data;
};

export const revokeTrackingConsent = async (projectId, reason = 'user_opt_out') => {
  const response = await api.post('/field-operations/tracking/consent/revoke', {
    project_id: projectId,
    reason,
  });
  return response.data;
};

export const getTrackingConfig = async (projectId) => {
  const response = await api.get('/field-operations/tracking/config', {
    params: { project_id: projectId },
  });
  return response.data;
};

export const registerTrackingDevice = async (payload) => {
  const response = await api.post('/field-operations/tracking/devices/register', payload);
  return response.data;
};

export const startTrackingSession = async (payload) => {
  const response = await api.post('/field-operations/tracking/sessions/start', payload);
  return response.data;
};

export const stopTrackingSession = async (payload) => {
  const response = await api.post('/field-operations/tracking/sessions/stop', payload);
  return response.data;
};

/**
 * Link a user account to a personnel record
 */
export const linkUserToPersonnel = async (personnelId, userId) => {
  const response = await api.post('/field-operations/link-user', {
    personnel_id: personnelId,
    user_id: userId
  });
  return response.data;
};

/**
 * Get available users for linking to personnel
 */
export const getAvailableUsers = async () => {
  const response = await api.get('/field-operations/available-users');
  return response.data;
};

// =====================================================
// REFERENCE MARKERS  (Base Camp / Fly Camp)
// =====================================================

export const getReferenceMarkers = async (projectId) => {
  const response = await api.get(`/field-operations/markers/${projectId}`);
  return response.data;
};

export const createReferenceMarker = async (projectId, data) => {
  const response = await api.post(`/field-operations/markers/${projectId}`, data);
  return response.data;
};

export const updateReferenceMarker = async (markerId, data) => {
  const response = await api.put(`/field-operations/markers/${markerId}`, data);
  return response.data;
};

export const deleteReferenceMarker = async (markerId) => {
  const response = await api.delete(`/field-operations/markers/${markerId}`);
  return response.data;
};

// =====================================================
// MAP SETTINGS  (zoom limits / bounds)
// =====================================================

export const getMapSettings = async (projectId) => {
  const response = await api.get(`/field-operations/map-settings/${projectId}`);
  return response.data;
};

export const updateMapSettings = async (projectId, data) => {
  const response = await api.put(`/field-operations/map-settings/${projectId}`, data);
  return response.data;
};

// =====================================================
// ROAD TRACKS  (KML / GPX road / track overlays)
// =====================================================

export const getRoadTracks = async (projectId) => {
  const response = await api.get(`/field-operations/roads/${projectId}`);
  return response.data;
};

export const uploadRoadTrack = async (projectId, file, { name, description, color, lineWidth } = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  if (name) formData.append('name', name);
  if (description) formData.append('description', description);
  if (color) formData.append('color', color);
  if (lineWidth) formData.append('line_width', String(lineWidth));
  const response = await api.post(`/field-operations/roads/${projectId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const updateRoadTrack = async (roadId, { name, description, color, lineWidth, file } = {}) => {
  const formData = new FormData();
  if (name !== undefined) formData.append('name', name);
  if (description !== undefined) formData.append('description', description);
  if (color !== undefined) formData.append('color', color);
  if (lineWidth !== undefined) formData.append('line_width', String(lineWidth));
  if (file) formData.append('file', file);
  const response = await api.put(`/field-operations/roads/${roadId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const deleteRoadTrack = async (roadId) => {
  const response = await api.delete(`/field-operations/roads/${roadId}`);
  return response.data;
};

// =====================================================
// FIELD WORK PLANNING (daily drill plan / supervisor routes)
// =====================================================

export const getFieldWorkGroups = async (projectId) => {
  const response = await api.get('/field-operations/planning/work-groups', {
    params: { project_id: projectId },
  });
  return response.data;
};

export const createFieldWorkGroup = async (projectId, name, planRingColor) => {
  const response = await api.post('/field-operations/planning/work-groups', {
    project_id: projectId,
    name,
    plan_ring_color: planRingColor || '#EAB308',
  });
  return response.data;
};

export const updateFieldWorkGroup = async (groupId, { name, plan_ring_color: planRingColor } = {}) => {
  const body = {};
  if (name !== undefined) body.name = name;
  if (planRingColor !== undefined) body.plan_ring_color = planRingColor;
  const response = await api.put(`/field-operations/planning/work-groups/${groupId}`, body);
  return response.data;
};

export const deleteFieldWorkGroup = async (groupId) => {
  const response = await api.delete(`/field-operations/planning/work-groups/${groupId}`);
  return response.data;
};

export const getFieldPlanningSupervisorCandidates = async (projectId) => {
  const response = await api.get('/field-operations/planning/supervisor-candidates', {
    params: { project_id: projectId },
  });
  return response.data;
};

/**
 * Pending SPs with coordinates for plan builder.
 * @param {number} projectId
 * @param {{ planDate?: string, excludePlanId?: number }} [opts] planDate = YYYY-MM-DD for same-day exclusivity hints
 */
export const getFieldPlanningPendingSp = async (projectId, { planDate, excludePlanId } = {}) => {
  const params = { project_id: projectId };
  if (planDate) params.plan_date = planDate;
  if (excludePlanId != null) params.exclude_plan_id = excludePlanId;
  const response = await api.get('/field-operations/planning/pending-sp', { params });
  return response.data;
};

export const getFieldDailyPlans = async (projectId, { startDate, endDate } = {}) => {
  const params = { project_id: projectId };
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const response = await api.get('/field-operations/planning/daily-plans', { params });
  return response.data;
};

export const createFieldDailyPlan = async (payload) => {
  const response = await api.post('/field-operations/planning/daily-plans', payload);
  return response.data;
};

export const updateFieldDailyPlan = async (planId, { sp_ids, secondary_sp_ids = [], vehicle_assigned, plan_obs }) => {
  const response = await api.put(`/field-operations/planning/daily-plans/${planId}`, {
    sp_ids,
    secondary_sp_ids: secondary_sp_ids || [],
    vehicle_assigned: vehicle_assigned != null && String(vehicle_assigned).trim() !== '' ? String(vehicle_assigned).trim() : null,
    plan_obs: plan_obs != null && String(plan_obs).trim() !== '' ? String(plan_obs).trim() : null,
  });
  return response.data;
};

export const deleteFieldDailyPlan = async (planId) => {
  const response = await api.delete(`/field-operations/planning/daily-plans/${planId}`);
  return response.data;
};

/** Who gets all crews on the Field Operations map on web/desktop (admin, superadmin, or listed personnel). */
export const getFieldPlanningMapAccess = async (projectId) => {
  const response = await api.get('/field-operations/planning/map-plan-access', {
    params: { project_id: projectId },
  });
  return response.data;
};

/** Admin/superadmin: list personnel who can see every supervisor's plan on the map. */
export const getFieldPlanningGlobalViewers = async (projectId) => {
  const response = await api.get('/field-operations/planning/global-plan-viewers', {
    params: { project_id: projectId },
  });
  return response.data;
};

/** Full Project Personnel roster for the “Full map visibility” picker (all assignments, any login status). */
export const getFieldPlanningGlobalViewerCandidates = async (projectId) => {
  const response = await api.get('/field-operations/planning/global-plan-viewer-candidates', {
    params: { project_id: projectId },
  });
  return response.data;
};

export const addFieldPlanningGlobalViewer = async (projectId, personnelId) => {
  const response = await api.post('/field-operations/planning/global-plan-viewers', {
    project_id: projectId,
    personnel_id: personnelId,
  });
  return response.data;
};

export const deleteFieldPlanningGlobalViewer = async (viewerRowId) => {
  const response = await api.delete(`/field-operations/planning/global-plan-viewers/${viewerRowId}`);
  return response.data;
};

/** Map overlay payload for Field Operations (scope: 'my' | 'all') */
export const getFieldPlansForMap = async (projectId, planDate, scope = 'my') => {
  const response = await api.get('/field-operations/planning/for-map', {
    params: { project_id: projectId, plan_date: planDate, scope },
  });
  return response.data;
};

export const downloadFieldDailyPlanPdf = async (planId) => {
  const response = await api.get(`/field-operations/planning/daily-plans/${planId}/pdf`, {
    responseType: 'blob',
  });
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `field-plan-${planId}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
